import React, { useState, useEffect } from "react";
import AppleUI from "./AppleUI";
import { menu } from "../../utils/Customer/CustomerData";

interface PaymentOptionsProps {
    onlyCard?: boolean;
    onSuccess?: (methodToken: string) => void; 
    amount?: number;
    addressDetails?: any;
}

const PaymentOptions: React.FC<PaymentOptionsProps> = ({ 
    onlyCard, 
    onSuccess, 
    amount 
}) => {
    const [selected, setSelected] = useState("card");

    useEffect(() => {
        if (onlyCard) {
            setSelected("card");
        }
    }, [onlyCard]);

    const handleChildPaymentSubmit = () => {
        if (onSuccess) {
            onSuccess(selected); 
        }
    };

    // Filter menu options to strictly include only Card and Apple Pay
    const allowedMethods = menu.filter(item => item.id === "card" || item.id === "apple");

    return (
        <div className="border border-slate-200 bg-white rounded-2xl overflow-hidden shadow-3xs text-left">
            
            <h2 className="text-base font-black p-4 text-[#2F5E2E] border-b border-slate-100 tracking-tight uppercase">
                Choose Payment Mode
            </h2>

            <div className="flex flex-col md:flex-row min-h-[320px]">

                {/* LEFT RAIL CONTROL STRIP INDEX SELECTION */}
                <div className="w-full md:w-[38%] bg-slate-50/50 border-r border-slate-100 select-none">
                    {allowedMethods.map((item) => {
                        const isRestrictedMode = onlyCard && item.id !== "card";
                        const isCurrentActive = selected === item.id;
                        
                        return (
                            <div
                                key={item.id}
                                onClick={() => {
                                    if (isRestrictedMode) return;
                                    setSelected(item.id);
                                }}
                                className={`flex items-center gap-3 px-4 py-3.5 border-b border-slate-100/70 transition last:border-b-0
                                    ${isRestrictedMode ? "opacity-40 bg-slate-100/50 cursor-not-allowed" : "cursor-pointer"}
                                    ${isCurrentActive ? "bg-white border-l-4 border-l-[#2F5E2E] font-bold" : "hover:bg-slate-100/60"}
                                `}
                            >
                                {"image" in item ? (
                                    <img
                                        src={item.image}
                                        alt={item.label}
                                        className="w-6 h-6 object-contain shrink-0"
                                    />
                                ) : (
                                    <item.icon className={`w-4 h-4 shrink-0 ${isCurrentActive ? "text-[#2F5E2E]" : "text-slate-400"}`} />
                                )}

                                <span className={`text-xs tracking-wide ${isCurrentActive ? "text-slate-900 font-black" : "text-slate-600 font-semibold"}`}>
                                    {item.label}
                                </span>

                                <div className="ml-auto">
                                    <div className={`w-3.5 h-3.5 rounded-full border flex items-center justify-center transition
                                        ${isCurrentActive ? "border-[#2F5E2E]" : "border-slate-300"}
                                    `}>
                                        {isCurrentActive && (
                                            <div className="w-1.5 h-1.5 bg-[#2F5E2E] rounded-full" />
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* RIGHT RAIL ACTIONS VIEWS INJECTIONS FRAME */}
                <div className="w-full md:w-[62%] p-5 sm:p-6 bg-white flex flex-col justify-center items-center text-center">
                    
                    {/* CARD VIEW: Instant Proceed button instead of input forms */}
                    {selected === "card" && (
                        <div className="w-full max-w-xs flex flex-col gap-4">
                            <div>
                                <p className="text-sm font-medium text-slate-500">Amount to Pay</p>
                                <p className="text-2xl font-black text-slate-800">AED: {amount?.toFixed(2) || "0.00"}</p>
                            </div>
                            <button
                                onClick={handleChildPaymentSubmit}
                                className="w-full bg-[#2F5E2E] hover:bg-[#244a23] text-white text-sm font-bold py-3 px-4 rounded-xl transition duration-200 shadow-sm active:scale-[0.98]"
                            >
                                Proceed to Payment
                            </button>
                        </div>
                    )}

                    {/* APPLE PAY VIEW */}
                    {selected === "apple" && (
                        <div className="w-full flex justify-center">
                            <AppleUI onSuccess={handleChildPaymentSubmit} amount={amount} />
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
};

export default PaymentOptions;